import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  clearCurrentProject,
  fetchHackathonProject,
  fetchHackathonProjects,
  resetQuery,
  setQuery,
  selectCurrentProject,
  selectHackathonProjectsList,
  AppDispatch
} from '@/shared';
import { IHackathonQuery, SortOption } from '@/entities';
import { DEFAULT_PAGE_SIZE } from '@/constants';
import { mapSortToStrapi } from '@/utils';

type UseHackathonProjectsOptions = {
  documentId?: string;
  query?: IHackathonQuery;
  autoFetch?: boolean;
};

type HackathonListState = ReturnType<typeof selectHackathonProjectsList>;
type ProjectItem = HackathonListState['items'][number];
type ProjectId = string;

let hasUserSelectedSort = false;

const hackathonRandomOrder: Record<ProjectId, number> = {};
const LOAD_MORE_PAGE_SIZE = 10;

const getProjectId = (p: ProjectItem): string => {
  return String(p.documentId ?? p.id);
};

const getRandomWeightForProject = (project: ProjectItem): number => {
  const id = getProjectId(project);
  let weight = hackathonRandomOrder[id];
  if (weight === undefined) {
    weight = Math.random();
    hackathonRandomOrder[id] = weight;
  }
  return weight;
};

export const useHackathonProjects = (options: UseHackathonProjectsOptions = {}) => {
  const { documentId, query: initialQuery, autoFetch = true } = options;

  const dispatch = useDispatch<AppDispatch>();
  const listState = useSelector(selectHackathonProjectsList);
  const projectState = useSelector(selectCurrentProject);

  const mode = documentId ? 'single' : initialQuery || autoFetch ? 'list' : 'manual';
  const didInitRef = React.useRef(false);
  const stepRef = React.useRef<number>(initialQuery?.pageSize ?? DEFAULT_PAGE_SIZE);

  React.useEffect(() => {
    if (initialQuery?.sort && !hasUserSelectedSort) {
      hasUserSelectedSort = true;
    }
  }, [initialQuery?.sort]);

  React.useEffect(() => {
    if (mode === 'list' && autoFetch && !didInitRef.current) {
      didInitRef.current = true;

      const init: IHackathonQuery = {
        page: 1,
        pageSize: initialQuery?.pageSize ?? DEFAULT_PAGE_SIZE,
        ...(initialQuery ?? {})
      };

      dispatch(setQuery(init));
      dispatch(fetchHackathonProjects({ query: init, append: false }));
    }
  }, [dispatch, autoFetch, mode, initialQuery]);

  React.useEffect(() => {
    if (mode === 'single') {
      if (documentId) dispatch(fetchHackathonProject(documentId));
      else dispatch(clearCurrentProject());
      return () => {
        dispatch(clearCurrentProject());
      };
    }
  }, [dispatch, documentId, mode]);

  const updateQuery = (query: IHackathonQuery, opts?: { append?: boolean }) => {
    if (query.sort !== undefined) {
      hasUserSelectedSort = true;
    }

    const merged: IHackathonQuery = {
      ...listState.query,
      ...query
    };
    dispatch(setQuery(merged));
    dispatch(fetchHackathonProjects({ query: merged, append: !!opts?.append }));
  };

  const setSort = (option: SortOption) => {
    hasUserSelectedSort = true;

    const sort = mapSortToStrapi(option);
    const next: IHackathonQuery = {
      ...listState.query,
      page: 1,
      pageSize: LOAD_MORE_PAGE_SIZE,
      sort
    };
    dispatch(setQuery(next));
    dispatch(fetchHackathonProjects({ query: next, append: false }));
  };

  const refetchList = () => {
    dispatch(fetchHackathonProjects({ query: listState.query, append: false }));
  };

  const refetchProject = () => {
    if (documentId) dispatch(fetchHackathonProject(documentId));
  };

  const reset = () => {
    dispatch(resetQuery());
    const next: IHackathonQuery = {
      page: 1,
      pageSize: stepRef.current,
      sort: listState.query.sort
    };
    dispatch(setQuery(next));
    dispatch(fetchHackathonProjects({ query: next, append: false }));
  };

  const fetchProjects = (query?: IHackathonQuery, append = false) => {
    if (query?.sort !== undefined) {
      hasUserSelectedSort = true;
    }

    const merged: IHackathonQuery = { ...listState.query, ...(query ?? {}) };
    dispatch(setQuery(merged));
    dispatch(fetchHackathonProjects({ query: merged, append }));
  };

  const fetchProject = (docId: string) => {
    dispatch(fetchHackathonProject(docId));
  };

  const canLoadMore = (listState.meta?.total ?? 0) > (listState.items?.length ?? 0);

  const loadMore = React.useCallback(() => {
    if (listState.loading) return;
    const nextPage = (listState.query.page ?? listState.meta?.page ?? 1) + 1;
    const nextQuery: IHackathonQuery = {
      ...listState.query,
      page: nextPage,
      pageSize: LOAD_MORE_PAGE_SIZE
    };
    dispatch(setQuery(nextQuery));
    dispatch(fetchHackathonProjects({ query: nextQuery, append: true }));
  }, [dispatch, listState.loading, listState.meta?.page, listState.query]);

  const supportRank = React.useMemo(() => {
    const current = projectState.project;
    if (!current || !listState.items?.length) return undefined;
    const currentBalance = current.round1_balance ?? 0;
    const balances = listState.items.map((p) => p.round1_balance ?? 0);
    const greaterCount = balances.filter((b) => b > currentBalance).length;
    return greaterCount + 1;
  }, [projectState.project, listState.items]);

  const sortedItemsRef = React.useRef<ProjectItem[]>([]);

  const items = React.useMemo<ProjectItem[]>(() => {
    const baseItems = listState.items ?? [];
    if (!baseItems.length) {
      sortedItemsRef.current = baseItems;
      return baseItems;
    }

    if (hasUserSelectedSort) {
      sortedItemsRef.current = baseItems;
      return baseItems;
    }

    const prev = sortedItemsRef.current ?? [];
    const prevIdSet = new Set(prev.map(getProjectId));
    const currentById = new Map(baseItems.map((p) => [getProjectId(p), p]));

    const existingInOrder: ProjectItem[] = [];
    for (const old of prev) {
      const updated = currentById.get(getProjectId(old));
      if (updated) existingInOrder.push(updated);
    }

    const newItems: ProjectItem[] = [];
    for (const item of baseItems) {
      const id = getProjectId(item);
      if (!prevIdSet.has(id)) {
        newItems.push(item);
      }
    }

    const randomizedNew = [...newItems].sort(
      (a, b) => getRandomWeightForProject(a) - getRandomWeightForProject(b)
    );

    const result = [...existingInOrder, ...randomizedNew];
    sortedItemsRef.current = result;

    return result;
  }, [listState.items, hasUserSelectedSort]);

  if (mode === 'single') {
    return {
      project: projectState.project,
      loading: projectState.loading,
      error: projectState.error,
      supportRank,
      refetch: refetchProject,
      fetchProject,
      clearProject: () => dispatch(clearCurrentProject()),
      fetchProjects,
      updateQuery
    };
  }

  return {
    items,
    meta: listState.meta,
    query: listState.query,
    loading: listState.loading,
    error: listState.error,
    updateQuery,
    setSort,
    refetch: refetchList,
    reset,
    fetchProjects,
    fetchProject,
    canLoadMore,
    loadMore,
    project: projectState.project,
    projectLoading: projectState.loading,
    projectError: projectState.error,
    supportRank,
    clearProject: () => dispatch(clearCurrentProject())
  };
};
